// Newsletter subscribe. The ribbon dialog (rendered from the header) and the inline forms (the footer
// row and the blog sidebar block) all post to the same endpoint, so the POST and its status texts are
// shared. Follows navbar.ts: a DOMContentLoaded handler, an early return when the markup is
// absent, explicit types, no framework.
// subscribe query parameter opens the subscribe dialog on page load. It is then removed to prevent
// multiple dialog openings on refresh.


interface SubscribeResult {
    ok: boolean;
    message: string;
}

const invalidEmailMessage: string = "Enter a valid email address.";

document.addEventListener("DOMContentLoaded", function (): void {
    initDialog();

    document.querySelectorAll<HTMLElement>("[data-newsletter-inline]").forEach(initInlineForm);
});

function initDialog(): void {
    const openButton = document.querySelector<HTMLButtonElement>("[data-newsletter-open]");
    const dialog = document.querySelector<HTMLDialogElement>("[data-newsletter-dialog]");

    if (!openButton || !dialog) {
        return;
    }

    const form = dialog.querySelector<HTMLFormElement>("[data-newsletter-form]");
    const fields = dialog.querySelector<HTMLElement>("[data-newsletter-fields]");
    const emailInput = dialog.querySelector<HTMLInputElement>("#newsletter-email");
    const honeypotInput = dialog.querySelector<HTMLInputElement>("#newsletter-website");
    const submitButton = dialog.querySelector<HTMLButtonElement>("[data-newsletter-submit]");
    const status = dialog.querySelector<HTMLElement>("[data-newsletter-status]");
    const confirmation = dialog.querySelector<HTMLElement>("[data-newsletter-confirmation]");
    const tokenInput = dialog.querySelector<HTMLInputElement>("input[name='__RequestVerificationToken']");
    const closeButton = dialog.querySelector<HTMLButtonElement>("[data-newsletter-close]");

    if (!form || !fields || !emailInput || !submitButton || !status || !tokenInput) {
        return;
    }

    // <dialog>.showModal() is newer than the build baseline (safari 13.1, firefox 72). When it is
    // missing, fall back to the `open` attribute plus a class that centres the dialog as an overlay -
    // no focus trap, but the feature still works.
    const hasModal: boolean = typeof dialog.showModal === "function";

    openButton.addEventListener("click", openDialog);

    if (closeButton) {
        closeButton.addEventListener("click", closeDialog);
    }

    // Backdrop click (modal mode only): the event target is the <dialog> element itself when the
    // backdrop, rather than the form inside it, is clicked.
    dialog.addEventListener("click", function (event: MouseEvent): void {
        if (event.target === dialog) {
            closeDialog();
        }
    });

    // Native Escape handling fires "cancel"; keep the focus return consistent with closeDialog().
    dialog.addEventListener("cancel", function (): void {
        openButton.focus();
    });

    form.addEventListener("submit", function (event: Event): void {
        event.preventDefault();
        void submit();
    });

    // A link with the ?subscribe query parameter opens the dialog when the page loads. The parameter is
    // then removed from the URL, so a refresh or a shared link does not open the dialog again.
    const parameters: URLSearchParams = new URLSearchParams(window.location.search);

    if (parameters.has("subscribe")) {
        openDialog();
        parameters.delete("subscribe");

        const query: string = parameters.toString();
        const url: string = window.location.pathname + (query === "" ? "" : "?" + query) + window.location.hash;
        history.replaceState(null, "", url);
    }

    // ! assertions below: the null guards above hold, but TypeScript does not narrow closure-captured
    // variables (same reason as navbar.ts).

    function setStatus(message: string, isError: boolean): void {
        status!.textContent = message;
        status!.classList.toggle("newsletter-dialog__status-text--error", isError);
    }

    // Hides the input and the submit button, so only the confirmation message stays in the dialog. The
    // message itself is rendered by the view, so a content editor can change it.
    function showConfirmation(): void {
        fields!.hidden = true;
        setStatus("", false);

        if (confirmation) {
            confirmation.hidden = false;
        }
    }

    function openDialog(): void {
        // Undo a previous confirmation, so the dialog accepts another address when it is opened again.
        fields!.hidden = false;
        setStatus("", false);

        if (confirmation) {
            confirmation.hidden = true;
        }

        if (hasModal) {
            dialog!.showModal();
        } else {
            dialog!.setAttribute("open", "");
            dialog!.classList.add("newsletter-dialog--fallback");
        }

        emailInput!.focus();
    }

    function closeDialog(): void {
        if (hasModal) {
            dialog!.close();
        } else {
            dialog!.removeAttribute("open");
            dialog!.classList.remove("newsletter-dialog--fallback");
        }

        openButton!.focus();
    }

    async function submit(): Promise<void> {
        const email: string = emailInput!.value.trim();

        if (email === "" || !emailInput!.checkValidity()) {
            setStatus(invalidEmailMessage, true);
            emailInput!.focus();
            return;
        }

        submitButton!.disabled = true;
        setStatus("Subscribing…", false);

        const result: SubscribeResult = await postSubscription(
            email,
            honeypotInput ? honeypotInput.value : "",
            tokenInput!.value);

        if (result.ok) {
            form!.reset();
            showConfirmation();
        } else {
            setStatus(result.message, true);
        }

        submitButton!.disabled = false;
    }
}

function initInlineForm(root: HTMLElement): void {
    const form = root.querySelector<HTMLFormElement>("[data-newsletter-inline-form]");
    const status = root.querySelector<HTMLElement>("[data-newsletter-inline-status]");
    const confirmation = root.querySelector<HTMLElement>("[data-newsletter-inline-confirmation]");

    if (!form || !status) {
        return;
    }

    // Found by name, because each inline form has its own element ids.
    const emailInput = form.querySelector<HTMLInputElement>("input[name='email']");
    const honeypotInput = form.querySelector<HTMLInputElement>("input[name='website']");
    const submitButton = form.querySelector<HTMLButtonElement>("button[type='submit']");
    const tokenInput = form.querySelector<HTMLInputElement>("input[name='__RequestVerificationToken']");

    if (!emailInput || !submitButton || !tokenInput) {
        return;
    }

    form.addEventListener("submit", function (event: Event): void {
        event.preventDefault();
        void submit();
    });

    function setStatus(message: string, isError: boolean): void {
        status!.textContent = message;
        status!.classList.toggle("is-error", isError);
    }

    async function submit(): Promise<void> {
        const email: string = emailInput!.value.trim();

        if (email === "" || !emailInput!.checkValidity()) {
            setStatus(invalidEmailMessage, true);
            emailInput!.focus();
            return;
        }

        submitButton!.disabled = true;
        setStatus("Subscribing…", false);

        const result: SubscribeResult = await postSubscription(
            email,
            honeypotInput ? honeypotInput.value : "",
            tokenInput!.value);

        if (result.ok) {
            form!.reset();
            setStatus("", false);

            // The confirmation replaces the input row, so no stale input is left behind.
            if (confirmation) {
                form!.hidden = true;
                confirmation.hidden = false;
            }
        } else {
            setStatus(result.message, true);
        }

        submitButton!.disabled = false;
    }
}

async function postSubscription(email: string, website: string, token: string): Promise<SubscribeResult> {
    try {
        const response: Response = await fetch("/api/newsletter/subscribe", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "RequestVerificationToken": token,
            },
            body: JSON.stringify({ email: email, website: website }),
        });

        if (response.status === 202) {
            return { ok: true, message: "" };
        }

        if (response.status === 429) {
            return { ok: false, message: "Too many attempts. Please try again in a few minutes." };
        }

        return { ok: false, message: "Something went wrong. Please try again later." };
    } catch {
        return { ok: false, message: "Network error. Please try again later." };
    }
}
